import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PayrollDashboard.css';

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const [payrollData, setPayrollData] = useState({
    currentCycle: null,
    pendingPayRuns: [],
    approvedPayRuns: [],
    paymentHistory: [],
    systemStatus: 'idle',
    monthlyTotal: 0,
    upcomingDeadlines: []
  });
  
  const [selectedPayRun, setSelectedPayRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // eslint-disable-next-line no-unused-vars
  const [filter, setFilter] = useState('all');
  // eslint-disable-next-line no-unused-vars
  const [dateFilter, setDateFilter] = useState('');
  
  // New state for payroll generation
  const [upcomingPeriods, setUpcomingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [generatingData, setGeneratingData] = useState(false);
  const [csvGeneration, setCsvGeneration] = useState(false);



  // Expected payments state
  const [expectedPayments, setExpectedPayments] = useState({
    contractors: [],
    employees: [],
    loading: false
  });

  // Payment Order Manager state
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [paymentOrderMessage, setPaymentOrderMessage] = useState('');
  const [paymentOrderDataLoading, setPaymentOrderDataLoading] = useState(false); // Changed to false since we don't load by default
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [paymentOrderSearch, setPaymentOrderSearch] = useState('');
  const [paymentOrderTotals, setPaymentOrderTotals] = useState({
    totalAmount: 0,
    totalByStatus: 0,
    totalCount: 0
  });
  
  // Active filters state
  const [activeFilters, setActiveFilters] = useState({
    employmentType: null, // 'w2', '1099', or null
    status: null // 'draft', 'confirmed', 'unpaid', 'payment-pending', 'paid', 'failed', 'void', or null
  });
  
  // Bulk generation state
  const [bulkGenerationForm, setBulkGenerationForm] = useState({
    cutoffStartDate: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months ago
    cutoffEndDate: new Date().toISOString().split('T')[0], // today
    includeUnpaidInvoices: true
  });
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchPayrollData();
    fetchUpcomingPeriods();
    // Removed fetchPaymentOrders() - will only load when filters are selected
  }, []);

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch('/api/payroll/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPayrollData(data);
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      // Set default data structure on error
      setPayrollData({
        currentCycle: null,
        pendingPayRuns: [],
        approvedPayRuns: [],
        paymentHistory: [],
        systemStatus: 'error',
        monthlyTotal: 0,
        upcomingDeadlines: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingPeriods = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch('/api/payroll/upcoming-periods', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setUpcomingPeriods(data.periods || []);
    } catch (error) {
      console.error('Error fetching upcoming periods:', error);
    }
  };

  // Removed unused generatePayrollData function

  const generateCSVForPeriod = async (startDate, endDate, employmentType) => {
    setCsvGeneration(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payroll/generate-csv', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ startDate, endDate, employmentType })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Create and download the CSV file
      const csvContent = employmentType === 'w2' 
        ? generateJustWorksCSVContent(data.data)
        : generateBranchCSVContent(data.data);
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
              alert(`CSV generated successfully for ${employmentType === 'w2' ? 'W2 employees' : '1099 tutors'}!`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV');
    } finally {
      setCsvGeneration(false);
    }
  };

  const generateJustWorksCSVContent = (payrollData) => {
    const headers = [
      'First Name',
      'Last Name', 
      'Work Email',
      'Start Date',
      'End Date',
      'Pay Rate',
      'Regular Hour',
      'Paid Time Off',
      'Overtime Ho',
      'Double Time'
    ];
    
    const rows = [headers.join(',')];
    
    for (const [, data] of Object.entries(payrollData.contractors)) {
      const totalHours = data.orders.reduce((sum, order) => sum + (order.appointment_details?.units || 0), 0);
      const regularHours = Math.min(totalHours, 40);
      const overtimeHours = Math.max(0, totalHours - 40);
      const doubleTimeHours = 0; // Set to 0 for now
      const paidTimeOff = 0; // Set to 0 for now
      const payRate = totalHours > 0 ? data.total_amount / totalHours : 0;
      
      // Format dates as MM/DD/YY
      const startDate = formatDateForBranch(payrollData.start_date);
      const endDate = formatDateForBranch(payrollData.end_date);
      
      const row = [
        'Unknown', // Would be filled from contractor details
        'Employee',
        '', // Work email
        startDate,
        endDate,
        `$${payRate.toFixed(2)}`,
        regularHours.toFixed(2),
        paidTimeOff.toFixed(2),
        overtimeHours.toFixed(2),
        doubleTimeHours.toFixed(2)
      ];
      
      rows.push(row.join(','));
    }
    
    return rows.join('\n');
  };

  const generateBranchCSVContent = (payrollData) => {
    const headers = [
      'id',
      'amount',
      'disbursement_date_(yyyy-mm-dd)'
    ];
    
    const rows = [headers.join(',')];
    
    for (const [contractorId, data] of Object.entries(payrollData.contractors)) {
      // For now, use contractor ID as placeholder for Branch ID
      // This will be replaced with actual Branch IDs when they're added to the system
      const branchId = contractorId;
      
      // Format disbursement date as M/DD/YY (as shown in the example)
      const disbursementDate = formatDateForBranch(payrollData.end_date);
      
      const row = [
        branchId,
        Math.round(data.total_amount), // Amount as integer
        disbursementDate
      ];
      
      rows.push(row.join(','));
    }
    
    return rows.join('\n');
  };

  const formatDateForBranch = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year
    return `${month}/${day}/${year}`;
  };

  const approvePayRun = async (payRunId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/approve/${payRunId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchPayrollData();
        alert('Pay run approved successfully!');
      }
    } catch (error) {
      console.error('Error approving pay run:', error);
      alert('Error approving pay run');
    }
  };

  const rejectPayRun = async (payRunId, reason) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/reject/${payRunId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        fetchPayrollData();
        alert('Pay run rejected');
      }
    } catch (error) {
      console.error('Error rejecting pay run:', error);
      alert('Error rejecting pay run');
    }
  };

  const executePayments = async (payRunId) => {
    if (!window.confirm('Are you sure you want to execute payments? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/execute/${payRunId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchPayrollData();
        alert('Payments executed successfully!');
      }
    } catch (error) {
      console.error('Error executing payments:', error);
      alert('Error executing payments');
    }
  };

  const generateCSV = async (payRunId, type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/csv/${payRunId}?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-payrun-${payRunId}.csv`;
      a.click();
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV');
    }
  };

  // Removed unused getStatusColor function

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Function to generate deadlines from upcoming periods
  const generateDeadlinesFromPeriods = () => {
    const deadlines = [];
    const today = new Date();
    
    if (upcomingPeriods && upcomingPeriods.length > 0) {
      upcomingPeriods.forEach(period => {
        if (period.type === 'w2') {
          // W2 Employee deadlines
          const paymentOrderDate = new Date(period.paymentOrderDate);
          const timecardDeadline = new Date(period.timecardDeadline);
          
          // Payment Orders deadline
          if (paymentOrderDate > today) {
            const daysUntil = Math.ceil((paymentOrderDate - today) / (1000 * 60 * 60 * 24));
            deadlines.push({
              date: period.paymentOrderDate,
              description: `W2 Employee Payment Orders Due (${period.start} - ${period.end})`,
              status: daysUntil <= 2 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
              type: 'w2'
            });
          }
          
          // Timecard deadline
          if (timecardDeadline > today) {
            const daysUntil = Math.ceil((timecardDeadline - today) / (1000 * 60 * 60 * 24));
            deadlines.push({
              date: period.timecardDeadline,
              description: `W2 Employee JustWorks Submission (6:00 AM EST)`,
              status: daysUntil <= 1 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
              type: 'w2'
            });
          }
        } else if (period.type === '1099') {
          // 1099 Contractor deadlines
          const paymentOrderDate = new Date(period.paymentOrderDate);
          const paymentDate = period.paymentDate ? new Date(period.paymentDate) : null;
          
          // Payment Orders deadline
          if (paymentOrderDate > today) {
            const daysUntil = Math.ceil((paymentOrderDate - today) / (1000 * 60 * 60 * 24));
            deadlines.push({
              date: period.paymentOrderDate,
              description: `1099 Tutor Payment Orders Due (${period.start} - ${period.end})`,
              status: daysUntil <= 2 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
              type: '1099'
            });
          }
          
          // Payment execution date
          if (paymentDate && paymentDate > today) {
            const daysUntil = Math.ceil((paymentDate - today) / (1000 * 60 * 60 * 24));
            deadlines.push({
              date: period.paymentDate,
              description: `1099 Tutor Payments Execute (3 business days after review)`,
              status: daysUntil <= 1 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
              type: '1099'
            });
          }
        }
      });
    }
    
    // Sort by date (earliest first)
    deadlines.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Return only the next 6 deadlines
    return deadlines.slice(0, 6);
  };





  // Function to fetch expected payments for a specific period
  const fetchExpectedPayments = async (startDate, endDate, employmentType) => {
    setExpectedPayments(prev => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payroll/expected-payments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ startDate, endDate, employmentType })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (employmentType === '1099') {
          setExpectedPayments(prev => ({ 
            ...prev, 
            contractors: data.payments,
            loading: false 
          }));
        } else {
          setExpectedPayments(prev => ({ 
            ...prev, 
            employees: data.payments,
            loading: false 
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching expected payments:', error);
      setExpectedPayments(prev => ({ ...prev, loading: false }));
    }
  };

  // Removed unused fetchPaymentOrderContractors function

  const fetchPaymentOrders = async (filter = null, search = null, status = null) => {
    try {
      setPaymentOrderDataLoading(true);
      const token = localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InRlc3QtYWRtaW4taWQiLCJ1c2VybmFtZSI6ImFkbWluIiwicm9sZSI6ImFkbWluIiwiaWF0IjoxNzU0MDUzOTUzLCJleHAiOjE3NTQxNDAzNTN9.v1Xhh4DL8_6YTptM9iCeSr8o-ZOLRbiwkF6YCl3zD20';
      
      // Build query parameters
      const params = new URLSearchParams();
      if (filter) params.append('filter', filter);
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      
      const url = `/api/payroll/payment-orders${params.toString() ? `?${params.toString()}` : ''}`;
      console.log('Fetching payment orders:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Payment orders response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Payment orders error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Payment orders data:', data);
      
      if (data.success && data.paymentOrders) {
        console.log('Setting payment orders:', data.paymentOrders);
        setPaymentOrders(data.paymentOrders);
        setFilteredOrders(data.paymentOrders); // Set filtered orders to the same as payment orders since we're filtering server-side
        
        // Store totals for display
        if (data.totalAmount !== undefined) {
          setPaymentOrderTotals({
            totalAmount: data.totalAmount,
            totalByStatus: data.totalByStatus,
            totalCount: data.totalCount
          });
        }
        
        // Update the message with the results
        const filterText = status ? ` (Status: ${status})` : '';
        const totalText = data.totalByStatus ? 
          ` - Total: $${data.totalByStatus.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
        setPaymentOrderMessage(`Showing ${data.paymentOrders.length} payment orders${filterText}${totalText}`);
      } else {
        console.log('No payment orders found or invalid response');
        setPaymentOrders([]);
        setFilteredOrders([]);
        setPaymentOrderMessage('No payment orders found');
      }
    } catch (error) {
      console.error('Error fetching payment orders:', error);
      setPaymentOrders([]);
    } finally {
      setPaymentOrderDataLoading(false);
    }
  };

  // Removed unused createDraftPaymentOrder and raisePaymentOrder functions

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'warning',
      'raised': 'success',
      'paid': 'info',
      'cancelled': 'danger'
    };
    
    return (
      <span className={`status-badge ${statusColors[status] || 'secondary'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  // Bulk generation functions
  const generateBulkPaymentOrders = async (e) => {
    e.preventDefault();
    setBulkGenerating(true);
    setPaymentOrderMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payroll/generate-bulk-payment-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bulkGenerationForm)
      });

      const data = await response.json();
      
      if (data.success) {
        setPaymentOrderMessage(`✅ Generated ${data.generatedCount} payment orders successfully!`);
        fetchPaymentOrders(); // Refresh the list
        setSelectedOrders([]); // Clear selections
      } else {
        setPaymentOrderMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleOrderSelection = (orderId, checked) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const draftOrderIds = paymentOrders
        .filter(order => order.status === 'draft')
        .map(order => order.paymentOrderId);
      setSelectedOrders(draftOrderIds);
    } else {
      setSelectedOrders([]);
    }
  };

  const raiseSelectedOrders = async () => {
    if (selectedOrders.length === 0) {
      setPaymentOrderMessage('❌ Please select at least one payment order to raise.');
      return;
    }

    if (!window.confirm(`Are you sure you want to raise ${selectedOrders.length} payment orders? This will submit them for processing.`)) {
      return;
    }

    setBulkActionLoading(true);
    setPaymentOrderMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payroll/raise-bulk-payment-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paymentOrderIds: selectedOrders })
      });

      const data = await response.json();
      
      if (data.success) {
        setPaymentOrderMessage(`✅ Successfully raised ${data.raisedCount} payment orders!`);
        fetchPaymentOrders(); // Refresh the list
        setSelectedOrders([]); // Clear selections
      } else {
        setPaymentOrderMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getPaymentOrderStats = () => {
    const draftOrders = paymentOrders.filter(order => order.status === 'draft');
    const totalAmount = draftOrders.reduce((sum, order) => sum + (order.amount || 0), 0);
    
    return {
      count: draftOrders.length,
      amount: totalAmount
    };
  };

  // Sync payment orders from TutorCruncher
  const syncPaymentOrder = async (paymentOrderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/sync-payment-order/${paymentOrderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setPaymentOrderMessage(`✅ Payment order ${paymentOrderId} synced successfully!`);
        fetchPaymentOrders(); // Refresh the list
      } else {
        setPaymentOrderMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    }
  };

  const syncRecentPaymentOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payroll/sync-recent-payment-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ days: 30 })
      });

      const data = await response.json();

      if (data.success) {
        setPaymentOrderMessage('🔄 Payment order sync started... polling for completion.');
        // Poll for completion
        const pollStatus = async () => {
          try {
            const statusRes = await fetch('/api/payroll/sync-recent-payment-orders/status', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const statusData = await statusRes.json();
            if (statusData.status === 'running') {
              setPaymentOrderMessage('🔄 Sync in progress...');
              setTimeout(pollStatus, 3000);
            } else if (statusData.status === 'complete' && statusData.success) {
              setPaymentOrderMessage(`✅ Synced ${statusData.syncedCount} payment orders from last 30 days!`);
              fetchPaymentOrders();
            } else if (statusData.status === 'complete') {
              setPaymentOrderMessage(`❌ Sync failed: ${statusData.error}`);
            } else {
              setPaymentOrderMessage('✅ Sync complete.');
              fetchPaymentOrders();
            }
          } catch (err) {
            setPaymentOrderMessage(`❌ Error checking sync status: ${err.message}`);
          }
        };
        setTimeout(pollStatus, 3000);
      } else {
        setPaymentOrderMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    }
  };

  const generateJustWorksCSV = async (paymentOrderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/payroll/generate-justworks-from-payment-order/${paymentOrderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Create and download the CSV file
        const csvContent = data.csvContent || data.data.justworksData;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
        
        setPaymentOrderMessage(`✅ JustWorks CSV generated successfully for ${data.data.contractor.firstName} ${data.data.contractor.lastName}!`);
      } else {
        setPaymentOrderMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    }
  };

  const generateBranchCSV = async (paymentOrderId) => {
    try {
      setPaymentOrderMessage('🔄 Generating Branch CSV...');
      
      const token = localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InRlc3QtYWRtaW4taWQiLCJ1c2VybmFtZSI6ImFkbWluIiwicm9sZSI6ImFkbWluIiwiaWF0IjoxNzU0MDUzOTUzLCJleHAiOjE3NTQxNDAzNTN9.v1Xhh4DL8_6YTptM9iCeSr8o-ZOLRbiwkF6YCl3zD20';
      const response = await fetch(`/api/payroll/generate-branch-from-payment-order/${paymentOrderId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download CSV file
        const blob = new Blob([data.csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setPaymentOrderMessage(`✅ Branch CSV generated successfully: ${data.filename}`);
      } else {
        const errorData = await response.json();
        setPaymentOrderMessage(`❌ Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error generating Branch CSV:', error);
      setPaymentOrderMessage(`❌ Error: ${error.message}`);
    }
  };

  const generateUnpaidPaymentOrdersCSV = async (type = 'both') => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];

      if (type === 'both') {
        setPaymentOrderMessage('🔄 Generating CSVs for unpaid payment orders (server-side streaming)...');

        // Request Branch unpaid export
        const branchResp = await fetch(`/api/payroll/export-unpaid?type=branch`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (branchResp.ok) {
          const branchBlob = await branchResp.blob();
          const url = window.URL.createObjectURL(branchBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `branch-unpaid-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        }

        // Request JustWorks unpaid export
        const jwResp = await fetch(`/api/payroll/export-unpaid?type=justworks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (jwResp.ok) {
          const jwBlob = await jwResp.blob();
          const url = window.URL.createObjectURL(jwBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `justworks-unpaid-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        }

        setPaymentOrderMessage('✅ Exported both Branch and JustWorks CSVs');
      } else {
        setPaymentOrderMessage(`🔄 Generating ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV for unpaid payment orders...`);

        const resp = await fetch(`/api/payroll/export-unpaid?type=${type}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (resp.ok) {
          const blob = await resp.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${type}-unpaid-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
          
          // For Branch exports, check for skipped contractors and show alert
          if (type === 'branch') {
            // Get skipped contractors info
            const skippedResp = await fetch(`/api/payroll/skipped-contractors?type=branch`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (skippedResp.ok) {
              const skippedData = await skippedResp.json();
              if (skippedData.skippedContractors && skippedData.skippedContractors.length > 0) {
                const skippedNames = skippedData.skippedContractors.map(c => c.name).join(', ');
                const totalAmount = skippedData.skippedContractors.reduce((sum, c) => sum + (c.amount || 0), 0);
                
                alert(`⚠️ Branch CSV Export Complete\n\nSkipped contractors (no Branch ID):\n${skippedNames}\n\nTotal skipped: $${totalAmount.toFixed(2)}\n\nPlease pay these contractors via:\n• Wise (for 1099 contractors)\n• JustWorks (for W2 employees)`);
              }
            }
          }
          
          setPaymentOrderMessage(`✅ Exported ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV`);
        } else {
          throw new Error(`Failed to export ${type} CSV`);
        }
      }
    } catch (error) {
      console.error('Error generating unpaid payment orders CSV:', error);
      setPaymentOrderMessage(`❌ Error generating ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV`);
    }
  };

  const generatePaymentPendingOrdersCSV = async (type = 'both') => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];

      if (type === 'both') {
        setPaymentOrderMessage('🔄 Generating CSVs for payment-pending payment orders (server-side streaming)...');

        // Request Branch payment-pending export
        const branchResp = await fetch(`/api/payroll/export-payment-pending?type=branch`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (branchResp.ok) {
          const branchBlob = await branchResp.blob();
          const url = window.URL.createObjectURL(branchBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `branch-payment-pending-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        }

        // Request JustWorks payment-pending export
        const jwResp = await fetch(`/api/payroll/export-payment-pending?type=justworks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (jwResp.ok) {
          const jwBlob = await jwResp.blob();
          const url = window.URL.createObjectURL(jwBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `justworks-payment-pending-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        }

        setPaymentOrderMessage('✅ Exported both Branch and JustWorks CSVs');
      } else {
        setPaymentOrderMessage(`🔄 Generating ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV for payment-pending payment orders...`);

        const resp = await fetch(`/api/payroll/export-payment-pending?type=${type}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (resp.ok) {
          const blob = await resp.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${type}-payment-pending-orders-${today}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
          
          setPaymentOrderMessage(`✅ Exported ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV`);
        } else {
          throw new Error(`Failed to export ${type} CSV`);
        }
      }
    } catch (error) {
      console.error('Error generating payment-pending payment orders CSV:', error);
      setPaymentOrderMessage(`❌ Error generating ${type === 'branch' ? 'Branch' : 'JustWorks'} CSV`);
    }
  };

  const downloadCSV = (csvContent, filename) => {
    // Check if CSV content is valid (has more than just headers)
    const lines = csvContent.split('\n');
    if (lines.length <= 1) {
      console.error('CSV content is empty or invalid:', csvContent);
      alert('❌ Cannot generate CSV: No valid data found. Please check that contractors have Branch Worker IDs.');
      return;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // New functions for payment order CSV generation
  const generateJustWorksCSVFromPaymentOrders = (paymentOrders) => {
    const headers = [
      'First Name',
      'Last Name', 
      'Work Email',
      'Start Date',
      'End Date',
      'Pay Rate',
      'Regular Hour',
      'Paid Time Off',
      'Overtime Ho',
      'Double Time'
    ];
    
    const rows = [headers.join(',')];
    
    // Group orders by contractor
    const contractors = {};
    paymentOrders.forEach(order => {
      const contractorId = order.contractor?.id || order.contractor?._id;
      if (!contractors[contractorId]) {
        contractors[contractorId] = {
          contractor: order.contractor,
          orders: [],
          totalAmount: 0
        };
      }
      contractors[contractorId].orders.push(order);
      contractors[contractorId].totalAmount += order.amount || 0;
    });
    
    // Generate rows for each contractor
    Object.values(contractors).forEach(contractorData => {
      const contractor = contractorData.contractor;
      const totalAmount = contractorData.totalAmount;
      
      // Calculate hours (assuming 1 unit = 1 hour for now)
      const totalHours = contractorData.orders.reduce((sum, order) => sum + (order.appointment_details?.units || 1), 0);
      const regularHours = Math.min(totalHours, 40);
      const overtimeHours = Math.max(0, totalHours - 40);
      const doubleTimeHours = 0;
      const paidTimeOff = 0;
      const payRate = totalHours > 0 ? totalAmount / totalHours : 0;
      
      // Use current date for start/end dates in MM/DD/YY format for JustWorks
      const currentDate = new Date();
      const startDate = formatDateForBranch(currentDate.toISOString());
      const endDate = formatDateForBranch(currentDate.toISOString());
      
      const row = [
        contractor?.first_name || 'Unknown',
        contractor?.last_name || 'Employee',
        contractor?.email || '',
        startDate,
        endDate,
        `$${payRate.toFixed(2)}`,
        regularHours.toFixed(2),
        paidTimeOff.toFixed(2),
        overtimeHours.toFixed(2),
        doubleTimeHours.toFixed(2)
      ];
      
      rows.push(row.join(','));
    });
    
    return rows.join('\n');
  };

  const generateBranchCSVFromPaymentOrders = (paymentOrders) => {
    const headers = [
      'id',
      'amount',
      'disbursement_date_(yyyy-mm-dd)'
    ];
    
    const rows = [headers.join(',')];
    
    // Group orders by contractor
    const contractors = {};
    paymentOrders.forEach(order => {
      const contractorId = order.contractor?.id || order.contractor?._id;
      if (!contractors[contractorId]) {
        contractors[contractorId] = {
          contractor: order.contractor,
          orders: [],
          totalAmount: 0
        };
      }
      contractors[contractorId].orders.push(order);
      contractors[contractorId].totalAmount += order.amount || 0;
    });
    
    // Generate rows for each contractor
    Object.values(contractors).forEach(contractorData => {
      const contractor = contractorData.contractor;
      const totalAmount = contractorData.totalAmount;
      
      // Use branchWorkerId from contractor data, fallback to contractor ID if not available
      const branchId = contractor?.branchWorkerId || contractor?.branch_id || contractor?.id || contractor?._id || 'unknown';
      
      // Validate that we have a proper branchWorkerId
      if (!contractor?.branchWorkerId) {
        console.warn(`Missing branchWorkerId for contractor: ${contractor?.first_name} ${contractor?.last_name}`);
        return; // Skip this contractor
      }
      
      // Use current date for disbursement date in YYYY-MM-DD format for Branch
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const disbursementDate = `${year}-${month}-${day}`;
      
      const row = [
        branchId,
        totalAmount.toFixed(2), // Amount with 2 decimal places
        disbursementDate
      ];
      
      rows.push(row.join(','));
    });
    
    const csvContent = rows.join('\n');
    console.log('Generated Branch CSV content:', csvContent);
    return csvContent;
  };

  // Function to apply all active filters
  const applyFilters = (orders) => {
    let filtered = [...orders];
    
    // Apply employment type filter
    if (activeFilters.employmentType) {
      filtered = filtered.filter(order => order.employmentType === activeFilters.employmentType);
    }
    
    // Apply status filter
    if (activeFilters.status) {
      filtered = filtered.filter(order => order.status === activeFilters.status);
    }
    
    return filtered;
  };

  // Function to update filters and apply them
  const updateFilter = async (filterType, value) => {
    setShowAllOrders(true);
    
    // Toggle filter if clicking the same value
    const newValue = activeFilters[filterType] === value ? null : value;
    
    // Update active filters first
    const updatedFilters = {
      ...activeFilters,
      [filterType]: newValue
    };
    
    setActiveFilters(updatedFilters);

    // Fetch payment orders with the updated filter
    setPaymentOrderMessage('Loading payment orders...');
    try {
      await fetchPaymentOrders(
        updatedFilters.employmentType, 
        paymentOrderSearch, 
        updatedFilters.status
      );
      
            // The filtered orders will be set in fetchPaymentOrders, so we don't need to apply additional filters here
      // The message will be updated after the data is loaded
    } catch (error) {
      console.error('Error loading payment orders:', error);
      setPaymentOrderMessage('Error loading payment orders');
    }
  };

  if (loading) {
    return (
      <div className="payroll-dashboard">
        <div className="loading">Loading payroll data...</div>
      </div>
    );
  }

  if (payrollData.systemStatus === 'error') {
    return (
      <div className="payroll-dashboard">
        <div className="error-message">
          <h2>⚠️ Error Loading Payroll Data</h2>
          <p>The payroll system is currently unavailable. Please try again later.</p>
          <button onClick={fetchPayrollData} className="refresh-btn">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Payroll Management</h1>
          <p className="text-gray-600">Manage payroll cycles and payment orders</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={() => setActiveTab('overview')} className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}>
            Overview
          </button>
          <button onClick={() => setActiveTab('payment-orders')} className={`tab-btn ${activeTab === 'payment-orders' ? 'active' : ''}`}>
            📋 Payment Orders
          </button>
          <button onClick={() => setActiveTab('periods')} className={`tab-btn ${activeTab === 'periods' ? 'active' : ''}`}>
            📅 Upcoming Periods
          </button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-section">
          <div className="status-cards">
            <div className="status-card current-cycles">
              <h3>Current Pay Cycles</h3>
              
              <div className="cycles-container">
                {/* 1099 Contractor Cycle */}
                <div 
                  className="cycle-info contractor-cycle clickable"
                  onClick={() => {
                    fetchExpectedPayments('2025-07-15', '2025-07-31', '1099');
                    setActiveTab('expected-contractors');
                  }}
                >
                  <h4>🟢 1099 Tutors</h4>
                  <div className="cycle-details">
                    <p><strong>Pay Period:</strong> {formatDate('2025-07-15')} - {formatDate('2025-07-31')}</p>
                    <p><strong>Payment Orders Due:</strong> {formatDate('2025-08-01')}</p>
                    <p><strong>Payment Date:</strong> {formatDate('2025-08-06')} (3 business days after review)</p>
                    <div className="cycle-status">
                      <span className="status-badge active">Active Cycle</span>
                      <span className="click-hint">Click to view expected payments</span>
                    </div>
                  </div>
                </div>
                
                {/* W2 Employee Cycle */}
                <div 
                  className="cycle-info employee-cycle clickable"
                  onClick={() => {
                    fetchExpectedPayments('2025-07-13', '2025-07-26', 'w2');
                    setActiveTab('expected-employees');
                  }}
                >
                  <h4>🔵 W2 Employees</h4>
                  <div className="cycle-details">
                    <p><strong>Pay Period:</strong> {formatDate('2025-07-13')} - {formatDate('2025-07-26')}</p>
                    <p><strong>Payment Orders Due:</strong> {formatDate('2025-07-27')}</p>
                    <p><strong>JustWorks Deadline:</strong> {formatDate('2025-07-29')} at 6:00 AM EST</p>
                    <div className="cycle-status">
                      <span className="status-badge active">Active Cycle</span>
                      <span className="click-hint">Click to view expected payments</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          <div className="upcoming-deadlines">
            <h3>📅 Upcoming Deadlines</h3>
            <div className="deadline-list">
              {generateDeadlinesFromPeriods().length > 0 ? (
                generateDeadlinesFromPeriods().map((deadline, index) => (
                  <div key={index} className={`deadline-item ${deadline.status}`}>
                    <span className="deadline-date">{formatDate(deadline.date)}</span>
                    <span className="deadline-description">{deadline.description}</span>
                    <span className={`deadline-status ${deadline.status}`}>
                      {deadline.status === 'urgent' ? 'Due Soon' : 
                       deadline.status === 'warning' ? 'Warning' : 'Upcoming'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="deadline-item">
                  <span className="deadline-description">No upcoming deadlines</span>
                  <span className="deadline-status normal">All Caught Up</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="pending-section">
          <h2>⏳ Pending Approval</h2>
          {(!payrollData.pendingPayRuns || payrollData.pendingPayRuns.length === 0) ? (
            <p>No pay runs pending approval</p>
          ) : (
            <div className="payrun-list">
              {payrollData.pendingPayRuns.map((payRun) => (
                <div key={payRun.id} className="payrun-card">
                  <div className="payrun-header">
                    <h3>{payRun.name}</h3>
                    <span className="payrun-type">{payRun.type}</span>
                    <span className="payrun-amount">{formatCurrency(payRun.totalAmount)}</span>
                  </div>
                  
                  <div className="payrun-details">
                    <p><strong>Period:</strong> {formatDate(payRun.startDate)} - {formatDate(payRun.endDate)}</p>
                    <p><strong>Tutors:</strong> {payRun.contractorCount}</p>
                    <p><strong>Created:</strong> {formatDate(payRun.createdAt)}</p>
                  </div>

                  <div className="payrun-actions">
                    <button 
                      onClick={() => setSelectedPayRun(payRun)}
                      className="view-btn"
                    >
                      👁️ View Details
                    </button>
                    <button 
                      onClick={() => generateCSV(payRun.id, payRun.type === 'contractor' ? 'branch' : 'justworks')}
                      className="csv-btn"
                    >
                      📊 Download CSV
                    </button>
                    <button 
                      onClick={() => approvePayRun(payRun.id)}
                      className="approve-btn"
                    >
                      ✅ Approve
                    </button>
                    <button 
                      onClick={() => {
                        const reason = window.prompt('Reason for rejection:');
                        if (reason) rejectPayRun(payRun.id, reason);
                      }}
                      className="reject-btn"
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="history-section">
          <h2>📋 Payment History</h2>
          <div className="history-filters">
            <select onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Payments</option>
              <option value="contractor">1099 Tutors</option>
              <option value="employee">W2 Employees</option>
            </select>
            <input 
              type="date" 
              onChange={(e) => setDateFilter(e.target.value)}
              placeholder="Filter by date"
            />
          </div>

          <div className="payment-history">
            {payrollData.paymentHistory?.map((payment) => (
              <div key={payment.id} className="payment-item">
                <div className="payment-info">
                  <h4>{payment.payRunName}</h4>
                  <p><strong>Amount:</strong> {formatCurrency(payment.amount)}</p>
                  <p><strong>Type:</strong> {payment.type}</p>
                  <p><strong>Date:</strong> {formatDate(payment.executedAt)}</p>
                </div>
                <div className="payment-status">
                  <span className={`status-badge ${payment.status}`}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'periods' && (
        <div className="periods-section">
          <h2>📅 Upcoming Pay Periods</h2>
          {upcomingPeriods.length === 0 ? (
            <p>No upcoming pay periods scheduled.</p>
          ) : (
            <div className="period-list">
              {upcomingPeriods.map((period, index) => (
                <div key={index} className="period-card">
                  <div className="period-header">
                    <h3>{period.description}</h3>
                    <span className={`period-type ${period.type === '1099' ? 'contractor' : period.type}`}>
                      {period.type === 'w2' ? 'W2 Employee' : '1099 Tutor'}
                    </span>
                  </div>
                  
                  <div className="period-details">
                    <p><strong>Period:</strong> {formatDate(period.start)} - {formatDate(period.end)}</p>
                    {period.type === 'w2' ? (
                      <>
                        <p><strong>Payment Orders Due:</strong> {formatDate(period.paymentOrderDate)}</p>
                        <p><strong>Timecard Deadline:</strong> {formatDate(period.timecardDeadline)}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Payment Orders Due:</strong> {formatDate(period.paymentOrderDate)}</p>
                        <p><strong>Payment Date:</strong> {formatDate(period.paymentDate)}</p>
                      </>
                    )}
                  </div>

                  <div className="period-actions">
                    <button 
                      onClick={() => {
                        setSelectedPeriod({
                          ...period,
                          startDate: period.start,
                          endDate: period.end,
                          employmentType: period.type === 'w2' ? 'w2' : '1099'
                        });
                        setActiveTab('generate');
                      }}
                      className="generate-btn"
                    >
                      🔧 Generate Payroll
                    </button>
                    <button 
                      onClick={() => {
                        const employmentType = period.type === 'w2' ? 'w2' : '1099';
                        generateCSVForPeriod(period.start, period.end, employmentType);
                      }}
                      className="csv-btn"
                    >
                      📊 Generate CSV
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

            {activeTab === 'payment-orders' && (
        <div style={{
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          padding: '30px',
          marginBottom: '20px'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333' }}>💰 Payment Orders Export</h2>
          
          {paymentOrderMessage && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '20px',
              backgroundColor: paymentOrderMessage.includes('successfully') ? '#d4edda' : '#f8d7da',
              color: paymentOrderMessage.includes('successfully') ? '#155724' : '#721c24',
              border: `1px solid ${paymentOrderMessage.includes('successfully') ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {paymentOrderMessage}
            </div>
          )}

          {paymentOrderDataLoading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
              fontSize: '16px'
            }}>
              Loading payment orders...
            </div>
          ) : (
            <>
              {/* Search Input */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>Search Payment Orders</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Search by payment order ID or tutor name..."
                    value={paymentOrderSearch}
                    onChange={(e) => setPaymentOrderSearch(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setShowAllOrders(true);
                        setPaymentOrderMessage('Searching payment orders...');
                        fetchPaymentOrders(null, e.target.value).then(() => {
                          setFilteredOrders([...paymentOrders]);
                          setPaymentOrderMessage(`Found ${paymentOrders.length} payment orders matching "${e.target.value}"`);
                        });
                      }
                    }}
                    style={{
                      flex: '1',
                      minWidth: '200px',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => {
                      setShowAllOrders(true);
                      setPaymentOrderMessage('Searching payment orders...');
                      fetchPaymentOrders(null, paymentOrderSearch).then(() => {
                        setFilteredOrders([...paymentOrders]);
                        setPaymentOrderMessage(`Found ${paymentOrders.length} payment orders matching "${paymentOrderSearch}"`);
                      });
                    }}
                    style={{
                      background: '#6d4aff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    🔍 Search
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>Filter Payment Orders</h3>
                
                {/* Employment Type Filters */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '10px', color: '#555', fontSize: '14px' }}>Employment Type:</h4>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button 
                  onClick={() => updateFilter('employmentType', 'w2')}
                  style={{
                    background: activeFilters.employmentType === 'w2' ? '#2d1b7a' : '#6d4aff',
                    color: '#fff',
                    border: activeFilters.employmentType === 'w2' ? '2px solid #1a0f4a' : 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: activeFilters.employmentType === 'w2' ? '0 2px 8px rgba(45, 27, 122, 0.3)' : 'none'
                  }}
                >
                  🔍 Show W2 Employees (JustWorks)
                </button>
                
                <button 
                  onClick={() => updateFilter('employmentType', '1099')}
                  style={{
                    background: activeFilters.employmentType === '1099' ? '#0d4f1a' : '#28a745',
                    color: '#fff',
                    border: activeFilters.employmentType === '1099' ? '2px solid #052e0f' : 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: activeFilters.employmentType === '1099' ? '0 2px 8px rgba(13, 79, 26, 0.3)' : 'none'
                  }}
                >
                  🔍 Show 1099 Tutors (Branch)
                </button>
                
                <button 
                  onClick={() => {
                    setActiveFilters({ employmentType: null, status: null });
                    setShowAllOrders(true);
                    setPaymentOrderMessage('Loading all payment orders...');
                    fetchPaymentOrders().then(() => {
                      setFilteredOrders([...paymentOrders]);
                      setPaymentOrderMessage(`Showing all ${paymentOrders.length} payment orders`);
                    });
                  }}
                  style={{
                    background: activeFilters.employmentType === null && activeFilters.status === null ? '#054d5a' : '#17a2b8',
                    color: '#fff',
                    border: activeFilters.employmentType === null && activeFilters.status === null ? '2px solid #033a44' : 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: activeFilters.employmentType === null && activeFilters.status === null ? '0 2px 8px rgba(5, 77, 90, 0.3)' : 'none'
                  }}
                >
                  🔍 Show All Payment Orders
                </button>
                

                
                <button
                  onClick={() => {
                    setPaymentOrderMessage('🔄 Syncing recent payment orders from TutorCruncher...');
                    syncRecentPaymentOrders();
                  }}
                  style={{
                    background: '#e67e22',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                  disabled={paymentOrderMessage.includes('🔄')}
                >
                  {paymentOrderMessage.includes('🔄') ? '⏳ Syncing...' : '🔄 Sync Recent Payment Orders'}
                </button>
                </div>

                {/* Status Filters */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '10px', color: '#555', fontSize: '14px' }}>Status:</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <button 
                      onClick={() => updateFilter('status', 'draft')}
                      style={{
                        background: activeFilters.status === 'draft' ? '#8b6914' : '#ffc107',
                        color: '#000',
                        border: activeFilters.status === 'draft' ? '2px solid #5a450d' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'draft' ? '0 2px 8px rgba(139, 105, 20, 0.3)' : 'none'
                      }}
                    >
                      📝 Draft
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'confirmed')}
                      style={{
                        background: activeFilters.status === 'confirmed' ? '#0a4f5a' : '#17a2b8',
                        color: '#fff',
                        border: activeFilters.status === 'confirmed' ? '2px solid #063a42' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'confirmed' ? '0 2px 8px rgba(10, 79, 90, 0.3)' : 'none'
                      }}
                    >
                      ✅ Confirmed
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'unpaid')}
                      style={{
                        background: activeFilters.status === 'unpaid' ? '#721c24' : '#dc3545',
                        color: '#fff',
                        border: activeFilters.status === 'unpaid' ? '2px solid #4a1419' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'unpaid' ? '0 2px 8px rgba(114, 28, 36, 0.3)' : 'none'
                      }}
                    >
                      ❌ Unpaid
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'payment-pending')}
                      style={{
                        background: activeFilters.status === 'payment-pending' ? '#8b3a00' : '#fd7e14',
                        color: '#fff',
                        border: activeFilters.status === 'payment-pending' ? '2px solid #5a2600' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'payment-pending' ? '0 2px 8px rgba(139, 58, 0, 0.3)' : 'none'
                      }}
                    >
                      ⏳ Payment-Pending
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'paid')}
                      style={{
                        background: activeFilters.status === 'paid' ? '#0d4f1a' : '#28a745',
                        color: '#fff',
                        border: activeFilters.status === 'paid' ? '2px solid #052e0f' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'paid' ? '0 2px 8px rgba(13, 79, 26, 0.3)' : 'none'
                      }}
                    >
                      💰 Paid
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'failed')}
                      style={{
                        background: activeFilters.status === 'failed' ? '#3a1d5e' : '#6f42c1',
                        color: '#fff',
                        border: activeFilters.status === 'failed' ? '2px solid #26123e' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'failed' ? '0 2px 8px rgba(58, 29, 94, 0.3)' : 'none'
                      }}
                    >
                      💥 Failed
                    </button>

                    <button 
                      onClick={() => updateFilter('status', 'void')}
                      style={{
                        background: activeFilters.status === 'void' ? '#343a40' : '#6c757d',
                        color: '#fff',
                        border: activeFilters.status === 'void' ? '2px solid #23272b' : 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: activeFilters.status === 'void' ? '0 2px 8px rgba(52, 58, 64, 0.3)' : 'none'
                      }}
                    >
                      🚫 Void
                    </button>
                  </div>
                </div>
                </div>
              </div>

              {/* Payment Orders Display */}
              {!paymentOrders || paymentOrders.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#666',
                  fontSize: '16px'
                }}>
                  No payment orders found in database.
                </div>
              ) : (
                <>
                  {paymentOrders.length > 0 && (
                    <div className="alert alert-info">
                      <strong>📊 Payment Orders Status:</strong> Found {paymentOrders.length} payment orders in database. 
                      Current statuses: {[...new Set(paymentOrders.map(order => order.status))].join(', ')}. 
                      {paymentOrders.every(order => order.status === 'paid' || order.status === 'void') && 
                        ' All orders are either paid or voided. To see unpaid orders, you may need to sync new payment orders from TutorCruncher.'}
                    </div>
                  )}
                  
                  {/* Payment Orders Summary */}
                  {filteredOrders.length > 0 && (
                    <div className="payment-orders-summary" style={{
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <strong>📋 Summary:</strong> Showing {filteredOrders.length} payment orders
                          {activeFilters.status && (
                            <span style={{ color: '#6c757d' }}> (Status: {activeFilters.status})</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                              ${paymentOrderTotals.totalByStatus ? paymentOrderTotals.totalByStatus.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Total Amount</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                              {filteredOrders.length}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Orders</div>
                          </div>
                          {/* CSV Download Buttons for Unpaid Orders */}
                          {activeFilters.status === 'unpaid' && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => generateUnpaidPaymentOrdersCSV('branch')}
                                style={{
                                  background: '#ff6b35',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                title="Download Branch CSV for 1099 tutors"
                              >
                                📄 Branch CSV
                              </button>
                              <button
                                onClick={() => generateUnpaidPaymentOrdersCSV('justworks')}
                                style={{
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                title="Download JustWorks CSV for W2 employees"
                              >
                                📄 JustWorks CSV
                              </button>
                            </div>
                          )}
                          {/* CSV Download Buttons for Payment-Pending Orders */}
                          {activeFilters.status === 'payment-pending' && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => generatePaymentPendingOrdersCSV('branch')}
                                style={{
                                  background: '#ff6b35',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                title="Download Branch CSV for 1099 tutors"
                              >
                                📄 Branch CSV
                              </button>
                              <button
                                onClick={() => generatePaymentPendingOrdersCSV('justworks')}
                                style={{
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                title="Download JustWorks CSV for W2 employees"
                              >
                                📄 JustWorks CSV
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!showAllOrders ? (
                    <div className="text-center" style={{ padding: '40px 20px' }}>
                      <h4>💰 Payment Orders Export</h4>
                      <p className="text-muted">Use the search bar above or select a filter below to view payment orders:</p>
                      <div style={{ marginTop: '20px' }}>
                        <p>• <strong>Search:</strong> Enter a payment order ID or tutor name to find specific orders</p>
                        <p>• <strong>W2 Employees (JustWorks):</strong> Filter for W2 employees to generate JustWorks CSV</p>
                        <p>• <strong>1099 Tutors (Branch):</strong> Filter for 1099 tutors to generate Branch CSV</p>
                        <p>• <strong>All Payment Orders:</strong> View all payment orders sorted by most recent first</p>
                      </div>
                    </div>
                  ) : (
                    <div className="orders-container">
                                            {filteredOrders.length > 0 ? (
                        filteredOrders.map(order => (
                          <div key={order.paymentOrderId} className="payment-order-item">
                            <div className="order-header">
                              <a 
                                href={`https://account.storytimechess.com/accounting/pos/${order.paymentOrderId}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="order-id-link"
                              >
                                Payment Order #{order.paymentOrderId}
                              </a>
                              {getStatusBadge(order.status)}
                            </div>
                            <div className="order-details">
                              <p><strong>Tutor:</strong> {order.contractor?.first_name} {order.contractor?.last_name} 
                                {order.contractor?.email && (
                                  <span style={{ color: '#6c757d', fontSize: '12px' }}> ({order.contractor.email})</span>
                                )}
                              </p>
                              <p><strong>Amount:</strong> ${order.amount?.toFixed(2)}</p>
                              <p><strong>Status:</strong> {order.status}</p>
                              <p><strong>Employment Type:</strong> {order.employmentType || 'Unknown'}</p>
                              {order.display_id && (
                                <p><strong>Display ID:</strong> {order.display_id}</p>
                              )}
                              {order.date_sent && (
                                <p><strong>Date Sent:</strong> {new Date(order.date_sent).toLocaleDateString()}</p>
                              )}
                              <div className="order-actions">
                                {order.employmentType === 'w2' && (
                                  <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => generateJustWorksCSV(order.paymentOrderId)}
                                    title="Generate JustWorks CSV for W2 employees"
                                  >
                                    📄 JustWorks CSV
                                  </button>
                                )}
                                {order.employmentType === '1099' && (
                                  <button
                                    className="btn btn-warning btn-sm"
                                    onClick={() => generateBranchCSV(order.paymentOrderId)}
                                    title="Generate Branch CSV for 1099 tutors"
                                  >
                                    📄 Branch CSV
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No payment orders match the selected filter.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}



      {activeTab === 'expected-contractors' && (
        <div className="expected-payments-section">
          <div className="section-header">
            <h2>💰 Expected 1099 Contractor Payments</h2>
            <button 
              onClick={() => setActiveTab('overview')} 
              className="back-btn"
            >
              ← Back to Overview
            </button>
          </div>
          <div className="period-info">
            <h3>Pay Period: {formatDate('2025-07-15')} - {formatDate('2025-07-31')}</h3>
            <p>Total Expected: {formatCurrency(expectedPayments.contractors.reduce((sum, payment) => sum + payment.amount, 0))}</p>
          </div>
          
          {expectedPayments.loading ? (
            <div className="loading">Loading expected payments...</div>
          ) : expectedPayments.contractors.length > 0 ? (
            <div className="payments-list">
              {expectedPayments.contractors.map((payment, index) => (
                <div key={index} className="payment-item">
                  <div className="contractor-info">
                    <h4>{payment.contractorName}</h4>
                    <p>{payment.contractorEmail}</p>
                    <span className="branch-id">Branch ID: {payment.branchWorkerId || 'Not assigned'}</span>
                  </div>
                  <div className="payment-details">
                    <p><strong>Hours:</strong> {payment.hours}</p>
                    <p><strong>Rate:</strong> {formatCurrency(payment.rate)}</p>
                    <p><strong>Amount:</strong> {formatCurrency(payment.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No expected payments found for this period.</p>
          )}
        </div>
      )}

      {activeTab === 'expected-employees' && (
        <div className="expected-payments-section">
          <div className="section-header">
            <h2>💰 Expected W2 Employee Payments</h2>
            <button 
              onClick={() => setActiveTab('overview')} 
              className="back-btn"
            >
              ← Back to Overview
            </button>
          </div>
          <div className="period-info">
            <h3>Pay Period: {formatDate('2025-07-13')} - {formatDate('2025-07-26')}</h3>
            <p>Total Expected: {formatCurrency(expectedPayments.employees.reduce((sum, payment) => sum + payment.amount, 0))}</p>
          </div>
          
          {expectedPayments.loading ? (
            <div className="loading">Loading expected payments...</div>
          ) : expectedPayments.employees.length > 0 ? (
            <div className="payments-list">
              {expectedPayments.employees.map((payment, index) => (
                <div key={index} className="payment-item">
                  <div className="contractor-info">
                    <h4>{payment.contractorName}</h4>
                    <p>{payment.contractorEmail}</p>
                    <span className="employee-status">W2 Employee</span>
                  </div>
                  <div className="payment-details">
                    <p><strong>Hours:</strong> {payment.hours}</p>
                    <p><strong>Rate:</strong> {formatCurrency(payment.rate)}</p>
                    <p><strong>Amount:</strong> {formatCurrency(payment.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No expected payments found for this period.</p>
          )}
        </div>
      )}

      {/* Pay Run Detail Modal */}
      {selectedPayRun && (
        <div className="modal-overlay" onClick={() => setSelectedPayRun(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPayRun.name}</h2>
              <button onClick={() => setSelectedPayRun(null)} className="close-btn">×</button>
            </div>
            
            <div className="modal-body">
              <div className="payrun-summary">
                <h3>Summary</h3>
                <p><strong>Total Amount:</strong> {formatCurrency(selectedPayRun.totalAmount)}</p>
                <p><strong>Contractors:</strong> {selectedPayRun.contractorCount}</p>
                <p><strong>Period:</strong> {formatDate(selectedPayRun.startDate)} - {formatDate(selectedPayRun.endDate)}</p>
              </div>

              <div className="contractor-breakdown">
                <h3>Contractor Breakdown</h3>
                <div className="contractor-list">
                  {selectedPayRun.contractors?.map((contractor) => (
                    <div key={contractor.id} className="contractor-item">
                      <div className="contractor-info">
                        <h4>{contractor.name}</h4>
                        <p>{contractor.email}</p>
                      </div>
                      <div className="contractor-payment">
                        <p><strong>Hours:</strong> {contractor.hours}</p>
                        <p><strong>Rate:</strong> {formatCurrency(contractor.rate)}</p>
                        <p><strong>Total:</strong> {formatCurrency(contractor.total)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  onClick={() => generateCSV(selectedPayRun.id, selectedPayRun.type === 'contractor' ? 'branch' : 'justworks')}
                  className="csv-btn"
                >
                  📊 Download CSV
                </button>
                {selectedPayRun.status === 'approved' && (
                  <button 
                    onClick={() => executePayments(selectedPayRun.id)}
                    className="execute-btn"
                  >
                    💳 Execute Payments
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollDashboard; 